import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DateRangePicker, getDateBoundaries } from '@/components/common/DateRangePicker';
import { API } from '@/utils/constants';
import { formatDate } from '@/utils/date';
import { HelpTooltip } from '@/components/HelpTooltip';

/**
 * Customers Management Page
 * CRUD operations for customers and viewing job history
 */
const CustomersPage = () => {
  const [customers, setCustomers] = useState([]);
  const [totalCustomers, setTotalCustomers] = useState(0);  // Total in DB matching current filter (for "Showing X of Y")
  const [showAll, setShowAll] = useState(false);  // User-toggled "Show all" — bypasses the 200-row cap
  const [searchTerm, setSearchTerm] = useState('');
  
  // Server-side sort state
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const onSortClick = (field) => {
    if (sortBy === field) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      // Numeric fields default to descending (biggest first); text defaults to ascending (A→Z)
      setSortDir(field === 'job_count' || field === 'total_spend' || field === 'created_at' ? 'desc' : 'asc');
    }
  };
  const sortArrow = (field) => sortBy !== field ? '⇅' : (sortDir === 'asc' ? '▲' : '▼');
  const [customerTypeFilter, setCustomerTypeFilter] = useState('all');
  // Global-style date range filter (matches Reports page)
  const [dateRange, setDateRange] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [revenueBreakdown, setRevenueBreakdown] = useState(null);  // {this_month, last_month, this_quarter, this_year, last_year, all_time}
  // Custom date range probe (per-customer "show me this slice")
  const [customRevStart, setCustomRevStart] = useState('');
  const [customRevEnd,   setCustomRevEnd]   = useState('');
  const [customRevResult, setCustomRevResult] = useState(null);  // {revenue, job_count} | null
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [settings, setSettings] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Statement state
  const [showStatementModal, setShowStatementModal] = useState(false);
  const [statementData, setStatementData] = useState(null);
  const [statementLoading, setStatementLoading] = useState(false);
  const [statementDateRange, setStatementDateRange] = useState('all');
  const [statementStartDate, setStatementStartDate] = useState('');
  const [statementEndDate, setStatementEndDate] = useState('');
  const [sendingStatement, setSendingStatement] = useState(false);

  useEffect(() => {
    fetchCustomers();
    fetchSettings();
    // eslint-disable-next-line
  }, []);

  // When deep-linked from Global Search (/customers?highlight=<id>), open that customer's detail
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const highlightId = params.get('highlight');
    if (highlightId) {
      viewCustomerDetails(highlightId);
    }
    // eslint-disable-next-line
  }, [location.search]);

  // Auto-refetch whenever date range, customer-type, search term, or "show all" toggle changes
  // Search is debounced so we don't hammer the API on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchCustomers();
    }, 250);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [dateRange, customStartDate, customEndDate, customerTypeFilter, searchTerm, showAll, sortBy, sortDir]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchCustomers = async () => {
    try { // eslint-disable-line
      setLoading(true);
      const { start, end } = getDateBoundaries(dateRange, customStartDate, customEndDate);
      const params = {};
      if (start) params.start_date = start;
      if (end) params.end_date = end;
      if (customerTypeFilter && customerTypeFilter !== 'all') params.customer_type = customerTypeFilter;
      if (searchTerm) params.search = searchTerm;
      // Cap to 200 unless user explicitly asked for everything — keeps the DOM snappy on 13k+ records
      params.limit = showAll ? 0 : 200;
      params.sort_by = sortBy;
      params.sort_dir = sortDir;
      const response = await axios.get(`${API}/customers/with-insights`, { params });
      // New response shape: { customers, total, returned }. Fall back to old array shape just in case.
      if (Array.isArray(response.data)) {
        setCustomers(response.data);
        setTotalCustomers(response.data.length);
      } else {
        setCustomers(response.data.customers || []);
        setTotalCustomers(response.data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line
  const handleSearch = async () => {
    await fetchCustomers();
  };

  const viewCustomerDetails = async (customerId) => {
    try {
      const [customerResponse, jobsResponse, revenueResponse] = await Promise.all([
        axios.get(`${API}/customers/${customerId}`),
        axios.get(`${API}/customers/${customerId}/jobs`),
        axios.get(`${API}/customers/${customerId}/revenue-breakdown`),
      ]);
      const allJobs = jobsResponse.data || [];
      // Scope the visible job history to the active date range so detail revenue matches the list summary
      const { start, end } = getDateBoundaries(dateRange, customStartDate, customEndDate);
      let jobs = allJobs;
      if (start || end) {
        jobs = jobs.filter(j => {
          const d = (j.date_in || '').slice(0, 10);
          if (start && d < start) return false;
          if (end && d > end) return false;
          return true;
        });
      }
      setSelectedCustomer({
        ...customerResponse.data,
        jobs,
        all_jobs: allJobs,  // raw — used by the custom date probe on the Revenue by Period card
      });
      setRevenueBreakdown(revenueResponse.data);
      setCustomRevStart('');
      setCustomRevEnd('');
      setCustomRevResult(null);
      setFormData(customerResponse.data);
    } catch (error) {
      console.error('Error fetching customer details:', error);
      toast.error('Failed to load customer details');
    }
  };

  const handleEdit = () => {
    setEditing(true);
  };

  const handleCancel = () => {
    setEditing(false);
    setFormData(selectedCustomer);
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API}/customers/${selectedCustomer.id}`, formData);
      toast.success('Customer updated successfully');
      setEditing(false);
      setSelectedCustomer({ ...selectedCustomer, ...formData });
      fetchCustomers();
    } catch (error) {
      console.error('Error updating customer:', error);
      toast.error('Failed to update customer');
    }
  };

  const handleDelete = async (customerId) => {
    if (!window.confirm('Are you sure you want to delete this customer? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API}/customers/${customerId}`);
      toast.success('Customer deleted successfully');
      setSelectedCustomer(null);
      fetchCustomers();
    } catch (error) {
      console.error('Error deleting customer:', error);
      toast.error('Failed to delete customer');
    }
  };

  // Statement functions
  const fetchStatement = async (customerId, dateRange = 'all', startDate = '', endDate = '') => {
    setStatementLoading(true);
    try {
      const params = new URLSearchParams({ date_range: dateRange });
      if (dateRange === 'custom' && startDate && endDate) {
        params.append('start_date', startDate);
        params.append('end_date', endDate);
      }
      const response = await axios.get(`${API}/customers/${customerId}/statement?${params}`);
      setStatementData(response.data);
    } catch (error) {
      console.error('Error fetching statement:', error);
      toast.error('Failed to load statement');
    } finally {
      setStatementLoading(false);
    }
  };

  const handleOpenStatement = () => {
    setShowStatementModal(true);
    setStatementDateRange('all');
    setStatementStartDate('');
    setStatementEndDate('');
    fetchStatement(selectedCustomer.id);
  };

  const handleStatementDateRangeChange = (value) => {
    setStatementDateRange(value);
    if (value !== 'custom') {
      fetchStatement(selectedCustomer.id, value);
    }
  };

  const handleApplyCustomDateRange = () => {
    if (statementStartDate && statementEndDate) {
      fetchStatement(selectedCustomer.id, 'custom', statementStartDate, statementEndDate);
    }
  };

  const handleEmailStatement = async () => {
    setSendingStatement(true);
    try {
      await axios.post(`${API}/customers/${selectedCustomer.id}/statement/email`);
      toast.success(`Statement emailed to ${selectedCustomer.email}`);
    } catch (error) {
      console.error('Error sending statement:', error);
      toast.error(error.response?.data?.detail || 'Failed to send statement');
    } finally {
      setSendingStatement(false);
    }
  };

  const handlePrintStatement = () => {
    if (!statementData) return;
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Statement - ${statementData.customer.name}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; color: #333; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #1e40af; padding-bottom: 20px; }
          .header h1 { color: #1e40af; margin: 0; }
          .header p { color: #6b7280; margin: 5px 0 0 0; }
          .customer-info { background: #f8fafc; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
          .customer-info h3 { margin: 0 0 10px 0; color: #1e40af; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background: #f1f5f9; padding: 10px; text-align: left; border-bottom: 2px solid #e2e8f0; }
          td { padding: 10px; border-bottom: 1px solid #e5e7eb; }
          .text-right { text-align: right; }
          .total-row { background: #1e40af; color: white; font-weight: bold; }
          .total-row td { padding: 12px 10px; }
          .footer { margin-top: 30px; text-align: center; color: #6b7280; font-size: 12px; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>STATEMENT OF ACCOUNT</h1>
          <p>${settings?.business_info?.business_name || ''}</p>
        </div>
        <div class="customer-info">
          <h3>Statement For:</h3>
          <p><strong>${statementData.customer.name}</strong></p>
          <p>${statementData.customer.address}</p>
          <p style="margin-top: 10px; color: #6b7280;">Statement Date: ${statementData.statement_date}</p>
        </div>
        <h3 style="color: #1e40af;">Outstanding Invoices</h3>
        <table>
          <thead>
            <tr>
              <th>Job #</th>
              <th>Your Ref</th>
              <th>Date</th>
              <th>Description</th>
              <th class="text-right">Amount</th>
              <th class="text-right">Paid</th>
              <th class="text-right">Balance</th>
            </tr>
          </thead>
          <tbody>
            ${statementData.items.map(item => `
              <tr>
                <td>${item.job_number}</td>
                <td>${item.customer_reference || '-'}</td>
                <td>${item.date}</td>
                <td>${item.description || '-'}</td>
                <td class="text-right">$${item.total_amount.toFixed(2)}</td>
                <td class="text-right">$${item.amount_paid.toFixed(2)}</td>
                <td class="text-right">$${item.balance_due.toFixed(2)}</td>
              </tr>
            `).join('')}
            <tr class="total-row">
              <td colspan="4" class="text-right">TOTAL AMOUNT DUE:</td>
              <td class="text-right">$${statementData.summary.total_amount.toFixed(2)}</td>
              <td class="text-right">$${statementData.summary.total_paid.toFixed(2)}</td>
              <td class="text-right">$${statementData.summary.total_due.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
        <div class="footer">
          <p>${settings?.business_info?.business_name || ''} | ${settings?.business_info?.phone || ''} | ${settings?.business_info?.email || ''}</p>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (loading && !customers.length) {
    return <div className="loading">Loading customers...</div>;
  }

  return (
    <div className="customers-container" data-testid="customers-page">
      {!selectedCustomer ? (
        <Card>
          <CardHeader>
            <CardTitle>
              Customers
              <HelpTooltip 
                content="View and manage all customers. Click a customer to see their details, job history, and total revenue. Customers are automatically created when you create a new job." 
                side="right"
              />
            </CardTitle>
            <CardDescription>Manage your customer database</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Date Range Filter — matches Reports page for uniformity */}
            <div style={{ marginBottom: '1.25rem', padding: '12px 14px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <DateRangePicker
                dateRange={dateRange}
                setDateRange={setDateRange}
                startDate={customStartDate}
                setStartDate={setCustomStartDate}
                endDate={customEndDate}
                setEndDate={setCustomEndDate}
                label="Date Range (filters by customer creation date)"
              />
            </div>

            {/* Segment Summary — total revenue & job count for the current filters */}
            {(() => {
              const totalJobs = customers.reduce((sum, c) => sum + (c.job_count || 0), 0);
              const totalSpend = customers.reduce((sum, c) => sum + (c.total_spend || 0), 0);
              const activeCount = customers.filter(c => (c.job_count || 0) > 0).length;
              const segmentLabel = customerTypeFilter === 'all' ? 'All customers' : customerTypeFilter;
              return (
                <div style={{ marginBottom: '1.25rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }} data-testid="customer-segment-summary">
                  <div style={{ padding: '12px 14px', background: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                    <div style={{ fontSize: '0.75rem', color: '#1e40af', fontWeight: 600, textTransform: 'uppercase' }}>Segment</div>
                    <div style={{ fontSize: '1.15rem', fontWeight: 700, color: '#0f172a' }}>{segmentLabel}</div>
                  </div>
                  <div style={{ padding: '12px 14px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                    <div style={{ fontSize: '0.75rem', color: '#15803d', fontWeight: 600, textTransform: 'uppercase' }}>Total Revenue</div>
                    <div style={{ fontSize: '1.15rem', fontWeight: 700, color: '#0f172a' }} data-testid="segment-revenue">${totalSpend.toFixed(2)}</div>
                  </div>
                  <div style={{ padding: '12px 14px', background: '#fefce8', borderRadius: '8px', border: '1px solid #fde68a' }}>
                    <div style={{ fontSize: '0.75rem', color: '#854d0e', fontWeight: 600, textTransform: 'uppercase' }}>Jobs in Range</div>
                    <div style={{ fontSize: '1.15rem', fontWeight: 700, color: '#0f172a' }}>{totalJobs}</div>
                  </div>
                  <div style={{ padding: '12px 14px', background: '#fdf4ff', borderRadius: '8px', border: '1px solid #f5d0fe' }}>
                    <div style={{ fontSize: '0.75rem', color: '#a21caf', fontWeight: 600, textTransform: 'uppercase' }}>Active in Range</div>
                    <div style={{ fontSize: '1.15rem', fontWeight: 700, color: '#0f172a' }}>{activeCount} of {customers.length}{totalCustomers > customers.length ? ` (showing ${customers.length} of ${totalCustomers.toLocaleString()} total)` : ''}</div>
                  </div>
                </div>
              );
            })()}

            <div className="search-container" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'end' }}>
              <div style={{ flex: '1', minWidth: '200px' }}>
                <Input
                  type="text"
                  placeholder="Search by name, mobile, or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  data-testid="customer-search-input"
                />
              </div>
              <div style={{ minWidth: '150px' }}>
                <Label htmlFor="customer-type-filter" className="text-sm mb-1 block">Customer Type</Label>
                <Select value={customerTypeFilter} onValueChange={setCustomerTypeFilter}>
                  <SelectTrigger id="customer-type-filter" data-testid="customer-type-filter">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="not_set">Not Set</SelectItem>
                    {(settings?.customer_types || ['End User', 'Partner']).map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleSearch} data-testid="customer-search-btn">Search</Button>
              {(searchTerm || customerTypeFilter !== 'all' || dateRange !== 'all') && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchTerm('');
                    setCustomerTypeFilter('all');
                    setDateRange('all');
                    setCustomStartDate('');
                    setCustomEndDate('');
                  }}
                >
                  Clear
                </Button>
              )}
            </div>

            {/* Result count + show-all toggle — keeps the page snappy on the 13k+ customer list */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.85rem', color: '#475569' }}>
              <span data-testid="customers-result-count">
                {totalCustomers > customers.length
                  ? <>Showing <strong>{customers.length}</strong> of <strong>{totalCustomers.toLocaleString()}</strong> customers — <em>type in the search box above to find any customer</em></>
                  : <>Showing <strong>{customers.length}</strong> {customers.length === 1 ? 'customer' : 'customers'}</>
                }
              </span>
              {totalCustomers > 200 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAll(s => !s)}
                  data-testid="customers-show-all-toggle"
                >
                  {showAll ? 'Show top 200 only (faster)' : `Show all ${totalCustomers.toLocaleString()} (may be slow)`}
                </Button>
              )}
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead onClick={() => onSortClick('name')}          style={{ cursor: 'pointer', userSelect: 'none' }} data-testid="cust-sort-name">          Name <span style={{ color: sortBy === 'name' ? '#2563eb' : '#94a3b8', marginLeft: '0.25rem' }}>{sortArrow('name')}</span></TableHead>
                  <TableHead onClick={() => onSortClick('mobile')}        style={{ cursor: 'pointer', userSelect: 'none' }} data-testid="cust-sort-mobile">        Mobile <span style={{ color: sortBy === 'mobile' ? '#2563eb' : '#94a3b8', marginLeft: '0.25rem' }}>{sortArrow('mobile')}</span></TableHead>
                  <TableHead onClick={() => onSortClick('email')}         style={{ cursor: 'pointer', userSelect: 'none' }} data-testid="cust-sort-email">         Email <span style={{ color: sortBy === 'email' ? '#2563eb' : '#94a3b8', marginLeft: '0.25rem' }}>{sortArrow('email')}</span></TableHead>
                  <TableHead onClick={() => onSortClick('suburb')}        style={{ cursor: 'pointer', userSelect: 'none' }} data-testid="cust-sort-suburb">        Suburb <span style={{ color: sortBy === 'suburb' ? '#2563eb' : '#94a3b8', marginLeft: '0.25rem' }}>{sortArrow('suburb')}</span></TableHead>
                  <TableHead onClick={() => onSortClick('customer_type')} style={{ cursor: 'pointer', userSelect: 'none' }} data-testid="cust-sort-customer_type"> Customer Type <span style={{ color: sortBy === 'customer_type' ? '#2563eb' : '#94a3b8', marginLeft: '0.25rem' }}>{sortArrow('customer_type')}</span></TableHead>
                  <TableHead onClick={() => onSortClick('job_count')}     style={{ cursor: 'pointer', userSelect: 'none' }} data-testid="cust-sort-job_count">     Jobs <span style={{ color: sortBy === 'job_count' ? '#2563eb' : '#94a3b8', marginLeft: '0.25rem' }}>{sortArrow('job_count')}</span></TableHead>
                  <TableHead onClick={() => onSortClick('total_spend')}   style={{ cursor: 'pointer', userSelect: 'none' }} data-testid="cust-sort-total_spend">   Total Spend <span style={{ color: sortBy === 'total_spend' ? '#2563eb' : '#94a3b8', marginLeft: '0.25rem' }}>{sortArrow('total_spend')}</span></TableHead>
                  <TableHead style={{ textAlign: 'center' }}>📮 Newsletter</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {customers
                  .filter((customer) => {
                    if (customerTypeFilter === 'all') return true;
                    if (customerTypeFilter === 'not_set') return !customer.customer_type;
                    return customer.customer_type === customerTypeFilter;
                  })
                  .map((customer) => (
                  <TableRow key={customer.id}>
                    <TableCell className="font-semibold">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {customer.name}
                        {customer.is_repeat_customer && (
                          <span 
                            style={{ 
                              backgroundColor: '#fef3c7', 
                              color: '#92400e', 
                              fontSize: '0.65rem', 
                              padding: '0.15rem 0.4rem', 
                              borderRadius: '9999px',
                              fontWeight: '600'
                            }}
                            title="3+ jobs with this customer"
                          >
                            ⭐ REPEAT
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{customer.mobile}</TableCell>
                    <TableCell>{customer.email}</TableCell>
                    <TableCell>{customer.suburb}</TableCell>
                    <TableCell>{customer.customer_type || '-'}</TableCell>
                    <TableCell>
                      <span style={{ 
                        backgroundColor: customer.job_count > 0 ? '#dbeafe' : '#f3f4f6', 
                        color: customer.job_count > 0 ? '#1e40af' : '#6b7280',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: '500'
                      }}>
                        {customer.job_count || 0}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span style={{ 
                        color: customer.total_spend > 0 ? '#16a34a' : '#6b7280',
                        fontWeight: customer.total_spend > 0 ? '600' : '400'
                      }}>
                        ${(customer.total_spend || 0).toFixed(2)}
                      </span>
                    </TableCell>
                    <TableCell style={{ textAlign: 'center' }}>
                      {/* Newsletter status — 3 states:
                          - Unsubscribed (hard opt-out, cannot re-enable from here — must clear via API)
                          - Consented (green tick)
                          - Not consented (grey circle — click to opt them in) */}
                      {customer.newsletter_unsubscribed ? (
                        <span
                          title={`Unsubscribed${customer.newsletter_unsubscribed_at ? ' on ' + new Date(customer.newsletter_unsubscribed_at).toLocaleDateString() : ''} — will NEVER be synced`}
                          data-testid={`cust-nl-unsub-${customer.id}`}
                          style={{ background: '#fee2e2', color: '#991b1b', padding: '0.15rem 0.5rem', borderRadius: '9999px', fontSize: '0.7rem', fontWeight: 600 }}
                        >
                          🚫 Unsubscribed
                        </span>
                      ) : customer.newsletter_consent ? (
                        <button
                          type="button"
                          title={`Consented${customer.newsletter_consent_at ? ' on ' + new Date(customer.newsletter_consent_at).toLocaleDateString() : ''}${customer.newsletter_consent_source ? ' via ' + customer.newsletter_consent_source : ''} — click for options`}
                          onClick={async () => {
                            // 2-step choice via native confirm: hard unsubscribe vs simple revoke.
                            // Hard unsubscribe = permanent flag, respects Spam Act opt-out.
                            // Revoke = removes consent but customer could opt back in later.
                            const hardUnsub = window.confirm(
                              `Newsletter action for ${customer.name}:\n\n` +
                              `• OK = Mark UNSUBSCRIBED (permanent — will NEVER be synced again, respects opt-out)\n` +
                              `• Cancel = No action`
                            );
                            if (!hardUnsub) return;
                            try {
                              await axios.put(`${API}/customers/${customer.id}`, {
                                newsletter_consent: false,
                                newsletter_unsubscribed: true,
                              });
                              toast.success(`${customer.name} marked unsubscribed`);
                              fetchCustomers();
                            } catch { toast.error('Failed to update'); }
                          }}
                          data-testid={`cust-nl-yes-${customer.id}`}
                          style={{ background: '#dcfce7', color: '#166534', padding: '0.15rem 0.5rem', borderRadius: '9999px', fontSize: '0.7rem', fontWeight: 600, border: 'none', cursor: 'pointer' }}
                        >
                          ✓ Consented
                        </button>
                      ) : (
                        <button
                          type="button"
                          title="Click to opt this customer into the newsletter"
                          onClick={async () => {
                            try {
                              await axios.put(`${API}/customers/${customer.id}`, { newsletter_consent: true, newsletter_consent_source: 'manual' });
                              toast.success(`${customer.name} added to newsletter list`);
                              fetchCustomers();
                            } catch { toast.error('Failed to update'); }
                          }}
                          data-testid={`cust-nl-no-${customer.id}`}
                          style={{ background: '#f1f5f9', color: '#64748b', padding: '0.15rem 0.5rem', borderRadius: '9999px', fontSize: '0.7rem', fontWeight: 500, border: '1px dashed #cbd5e1', cursor: 'pointer' }}
                        >
                          + Opt in
                        </button>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => viewCustomerDetails(customer.id)}
                        data-testid={`view-customer-${customer.id}`}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {customers.filter((customer) => {
              if (customerTypeFilter === 'all') return true;
              if (customerTypeFilter === 'not_set') return !customer.customer_type;
              return customer.customer_type === customerTypeFilter;
            }).length === 0 && (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                {customerTypeFilter !== 'all' 
                  ? `No customers found with type "${customerTypeFilter === 'not_set' ? 'Not Set' : customerTypeFilter}"`
                  : 'No customers found'}
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <CardTitle>{selectedCustomer.name}</CardTitle>
                <CardDescription>Customer Details & Job History</CardDescription>
              </div>
              <Button variant="outline" onClick={() => setSelectedCustomer(null)} data-testid="back-to-list-btn">
                Back to List
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="customer-detail-view">
              <div className="customer-info-section">
                <h3>Customer Information</h3>
                {!editing ? (
                  <div className="info-display">
                    <div className="info-row">
                      <span className="info-label">Name:</span>
                      <span className="info-value">{selectedCustomer.name}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Mobile:</span>
                      <span className="info-value">{selectedCustomer.mobile}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Email:</span>
                      <span className="info-value">{selectedCustomer.email}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Address:</span>
                      <span className="info-value">{selectedCustomer.address_line1}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Suburb:</span>
                      <span className="info-value">{selectedCustomer.suburb}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Postcode:</span>
                      <span className="info-value">{selectedCustomer.postcode}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">State:</span>
                      <span className="info-value">{selectedCustomer.state}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Company Name:</span>
                      <span className="info-value" data-testid="customer-info-company-name">{selectedCustomer.company_name || '-'}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">ABN:</span>
                      <span className="info-value" data-testid="customer-info-abn">{selectedCustomer.abn || '-'}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Customer Type:</span>
                      <span className="info-value">{selectedCustomer.customer_type || 'Not specified'}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Notes:</span>
                      <span className="info-value">{selectedCustomer.notes || 'No notes'}</span>
                    </div>
                    <div className="action-buttons" style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <Button onClick={handleEdit} data-testid="edit-customer-btn">Edit</Button>
                      <Button 
                        variant="outline" 
                        onClick={handleOpenStatement}
                        style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', color: 'white', border: 'none' }}
                        data-testid="generate-statement-btn"
                      >
                        📄 Generate Statement
                      </Button>
                      <Button variant="destructive" onClick={() => handleDelete(selectedCustomer.id)} data-testid="delete-customer-btn">
                        Delete Customer
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="edit-form">
                    <div className="form-field">
                      <Label>Name</Label>
                      <Input name="name" value={formData.name} onChange={handleChange} />
                    </div>
                    <div className="form-field">
                      <Label>Company Name</Label>
                      <Input
                        name="company_name"
                        value={formData.company_name || ''}
                        onChange={handleChange}
                        placeholder="Business customer's company (optional)"
                        data-testid="customer-edit-company-name"
                      />
                    </div>
                    <div className="form-field">
                      <Label>ABN</Label>
                      <Input
                        name="abn"
                        value={formData.abn || ''}
                        onChange={handleChange}
                        placeholder="Australian Business Number (optional)"
                        data-testid="customer-edit-abn"
                      />
                    </div>
                    <div className="form-field">
                      <Label>Mobile</Label>
                      <Input name="mobile" value={formData.mobile} onChange={handleChange} />
                    </div>
                    <div className="form-field">
                      <Label>Email</Label>
                      <Input name="email" type="email" value={formData.email} onChange={handleChange} />
                    </div>
                    <div className="form-field">
                      <Label>Address Line 1</Label>
                      <Input name="address_line1" value={formData.address_line1} onChange={handleChange} />
                    </div>
                    <div className="form-field">
                      <Label>Suburb</Label>
                      <Input name="suburb" value={formData.suburb} onChange={handleChange} />
                    </div>
                    <div className="form-field">
                      <Label>Postcode</Label>
                      <Input name="postcode" value={formData.postcode} onChange={handleChange} />
                    </div>
                    <div className="form-field">
                      <Label>State</Label>
                      <Input name="state" value={formData.state} onChange={handleChange} />
                    </div>
                    <div className="form-field">
                      <Label>Customer Type</Label>
                      <Select
                        value={formData.customer_type || ''}
                        onValueChange={(value) => setFormData({ ...formData, customer_type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select customer type" />
                        </SelectTrigger>
                        <SelectContent>
                          {(settings?.customer_types || ['End User', 'Partner']).map((type) => (
                            <SelectItem key={type} value={type}>{type}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="form-field">
                      <Label>Notes</Label>
                      <Textarea name="notes" value={formData.notes || ''} onChange={handleChange} rows={4} />
                    </div>
                    <div className="action-buttons" style={{ marginTop: '1rem' }}>
                      <Button onClick={handleSave} data-testid="save-customer-btn">Save Changes</Button>
                      <Button variant="outline" onClick={handleCancel} data-testid="cancel-edit-btn">Cancel</Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="customer-jobs-section" style={{ marginTop: '2rem' }}>
                {/* Revenue by Period — quick summary so users can answer "how much has DRC sent us lately?" in one glance */}
                {revenueBreakdown && (
                  <Card style={{ marginBottom: '1rem', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                    <CardHeader style={{ paddingBottom: '0.5rem' }}>
                      <CardTitle style={{ fontSize: '1rem', color: '#0f172a' }}>Revenue by Period</CardTitle>
                      <CardDescription style={{ fontSize: '0.75rem' }}>
                        Across all dates — independent of the page-level date filter below.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '0.5rem' }}>
                        {[
                          { key: 'this_month',   label: 'This Month' },
                          { key: 'last_month',   label: 'Last Month' },
                          { key: 'this_quarter', label: 'This Quarter' },
                          { key: 'this_year',    label: 'This Year' },
                          { key: 'last_year',    label: 'Last Year' },
                          { key: 'all_time',     label: 'All Time' },
                        ].map(({ key, label }) => {
                          const b = revenueBreakdown[key] || { revenue: 0, job_count: 0 };
                          const isAllTime = key === 'all_time';
                          return (
                            <div
                              key={key}
                              data-testid={`revenue-bucket-${key}`}
                              style={{
                                padding: '0.6rem 0.5rem',
                                backgroundColor: isAllTime ? '#dbeafe' : '#ffffff',
                                border: `1px solid ${isAllTime ? '#93c5fd' : '#e2e8f0'}`,
                                borderRadius: '0.4rem',
                                textAlign: 'center',
                              }}
                            >
                              <div style={{ fontSize: '0.65rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.2rem' }}>{label}</div>
                              <div style={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>
                                ${(b.revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                              </div>
                              <div style={{ fontSize: '0.7rem', color: '#475569' }}>
                                {b.job_count} {b.job_count === 1 ? 'job' : 'jobs'}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Custom date range probe — answers "how much in any window I pick" */}
                      <div style={{ marginTop: '0.75rem', padding: '0.6rem 0.75rem', backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '0.75rem', color: '#0f172a', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Custom Range:</span>
                        <Input
                          type="date"
                          value={customRevStart}
                          onChange={(e) => {
                            setCustomRevStart(e.target.value);
                            setCustomRevResult(null);
                          }}
                          style={{ width: '155px' }}
                          data-testid="custom-rev-start"
                        />
                        <span style={{ fontSize: '0.8rem', color: '#475569' }}>to</span>
                        <Input
                          type="date"
                          value={customRevEnd}
                          onChange={(e) => {
                            setCustomRevEnd(e.target.value);
                            setCustomRevResult(null);
                          }}
                          style={{ width: '155px' }}
                          data-testid="custom-rev-end"
                        />
                        <Button
                          size="sm"
                          onClick={() => {
                            const start = customRevStart;
                            const end   = customRevEnd;
                            if (!start && !end) {
                              toast.error('Pick at least a start or end date');
                              return;
                            }
                            const jobs = selectedCustomer?.all_jobs || [];
                            let total = 0;
                            let count = 0;
                            jobs.forEach(j => {
                              const d = (j.date_in || '').slice(0, 10);
                              if (!d) return;
                              if (start && d < start) return;
                              if (end && d > end) return;
                              total += (j.total_amount || j.subtotal || 0);
                              count += 1;
                            });
                            setCustomRevResult({ revenue: total, job_count: count });
                          }}
                          data-testid="custom-rev-go"
                        >
                          Calculate
                        </Button>
                        {customRevResult && (
                          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.75rem' }} data-testid="custom-rev-result">
                            <span style={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>
                              ${customRevResult.revenue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                            </span>
                            <span style={{ fontSize: '0.8rem', color: '#475569' }}>
                              {customRevResult.job_count} {customRevResult.job_count === 1 ? 'job' : 'jobs'}
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setCustomRevStart(''); setCustomRevEnd(''); setCustomRevResult(null); }}
                              data-testid="custom-rev-clear"
                            >
                              Clear
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                <h3>Job History ({selectedCustomer.jobs?.length || 0} jobs)</h3>
                {selectedCustomer.jobs && selectedCustomer.jobs.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Job #</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Job Type</TableHead>
                        <TableHead>Date In</TableHead>
                        <TableHead>Total</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedCustomer.jobs.map((job) => (
                        <TableRow key={job.id}>
                          <TableCell className="font-semibold">{job.job_number}</TableCell>
                          <TableCell>{job.status}</TableCell>
                          <TableCell>{job.job_type}</TableCell>
                          <TableCell>{formatDate(job.date_in)}</TableCell>
                          <TableCell>${parseFloat(job.total_amount || 0).toFixed(2)}</TableCell>
                          <TableCell>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/job/${job.id}`)}
                              data-testid={`view-job-${job.job_number}`}
                            >
                              View Job
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div style={{ padding: '1rem', textAlign: 'center', color: '#666' }}>
                    No jobs found for this customer
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Statement Modal */}
      <Dialog open={showStatementModal} onOpenChange={setShowStatementModal}>
        <DialogContent style={{ maxWidth: '900px', maxHeight: '90vh', overflow: 'auto' }}>
          <DialogHeader>
            <DialogTitle>Customer Statement</DialogTitle>
            <DialogDescription>
              Statement of outstanding invoices for {selectedCustomer?.name}
            </DialogDescription>
          </DialogHeader>
          
          {/* Date Range Filter */}
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'end', flexWrap: 'wrap', marginBottom: '1rem', padding: '1rem', background: '#f8fafc', borderRadius: '8px' }}>
            <div style={{ minWidth: '150px' }}>
              <Label>Date Range</Label>
              <Select value={statementDateRange} onValueChange={handleStatementDateRangeChange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="last7">Last 7 Days</SelectItem>
                  <SelectItem value="last30">Last 30 Days</SelectItem>
                  <SelectItem value="last90">Last 90 Days</SelectItem>
                  <SelectItem value="thisMonth">This Month</SelectItem>
                  <SelectItem value="lastMonth">Last Month</SelectItem>
                  <SelectItem value="thisYear">This Year</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {statementDateRange === 'custom' && (
              <>
                <div>
                  <Label>Start Date</Label>
                  <Input 
                    type="date" 
                    value={statementStartDate} 
                    onChange={(e) => setStatementStartDate(e.target.value)} 
                  />
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input 
                    type="date" 
                    value={statementEndDate} 
                    onChange={(e) => setStatementEndDate(e.target.value)} 
                  />
                </div>
                <Button onClick={handleApplyCustomDateRange} disabled={!statementStartDate || !statementEndDate}>
                  Apply
                </Button>
              </>
            )}
          </div>

          {statementLoading ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: '#6b7280' }}>
              Loading statement...
            </div>
          ) : statementData ? (
            <>
              {/* Customer Info */}
              <div style={{ background: '#f0f9ff', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
                <h4 style={{ margin: '0 0 0.5rem 0', color: '#1e40af' }}>Statement For:</h4>
                <p style={{ margin: '0', fontWeight: '600' }}>{statementData.customer.name}</p>
                <p style={{ margin: '0.25rem 0', color: '#6b7280', fontSize: '0.875rem' }}>{statementData.customer.address}</p>
                <p style={{ margin: '0.5rem 0 0 0', color: '#6b7280', fontSize: '0.875rem' }}>Statement Date: {statementData.statement_date}</p>
              </div>

              {/* Statement Items */}
              {statementData.items.length > 0 ? (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Job #</TableHead>
                        <TableHead>Customer Ref</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead style={{ textAlign: 'right' }}>Amount</TableHead>
                        <TableHead style={{ textAlign: 'right' }}>Paid</TableHead>
                        <TableHead style={{ textAlign: 'right' }}>Balance</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {statementData.items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <span 
                              style={{ color: '#2563eb', cursor: 'pointer', textDecoration: 'underline' }}
                              onClick={() => { setShowStatementModal(false); navigate(`/job/${item.job_id}`); }}
                            >
                              {item.job_number}
                            </span>
                          </TableCell>
                          <TableCell>{item.customer_reference || '-'}</TableCell>
                          <TableCell>{item.date}</TableCell>
                          <TableCell>{item.description || '-'}</TableCell>
                          <TableCell style={{ textAlign: 'right' }}>${item.total_amount.toFixed(2)}</TableCell>
                          <TableCell style={{ textAlign: 'right' }}>${item.amount_paid.toFixed(2)}</TableCell>
                          <TableCell style={{ textAlign: 'right', fontWeight: '600' }}>${item.balance_due.toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                      {/* Total Row */}
                      <TableRow style={{ background: '#1e40af', color: 'white' }}>
                        <TableCell colSpan={4} style={{ textAlign: 'right', fontWeight: '600', color: 'white' }}>
                          TOTAL AMOUNT DUE:
                        </TableCell>
                        <TableCell style={{ textAlign: 'right', fontWeight: '600', color: 'white' }}>
                          ${statementData.summary.total_amount.toFixed(2)}
                        </TableCell>
                        <TableCell style={{ textAlign: 'right', fontWeight: '600', color: 'white' }}>
                          ${statementData.summary.total_paid.toFixed(2)}
                        </TableCell>
                        <TableCell style={{ textAlign: 'right', fontWeight: '600', color: 'white' }}>
                          ${statementData.summary.total_due.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>

                  {/* Summary Cards */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginTop: '1.5rem' }}>
                    <div style={{ padding: '1rem', background: '#f0f9ff', borderRadius: '8px', textAlign: 'center' }}>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Total Invoices</div>
                      <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e40af' }}>{statementData.summary.total_invoices}</div>
                    </div>
                    <div style={{ padding: '1rem', background: '#f0fdf4', borderRadius: '8px', textAlign: 'center' }}>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Total Paid</div>
                      <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#16a34a' }}>${statementData.summary.total_paid.toFixed(2)}</div>
                    </div>
                    <div style={{ padding: '1rem', background: '#fef2f2', borderRadius: '8px', textAlign: 'center' }}>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Total Due</div>
                      <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#dc2626' }}>${statementData.summary.total_due.toFixed(2)}</div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    <Button variant="outline" onClick={handlePrintStatement}>
                      🖨️ Print Statement
                    </Button>
                    <Button 
                      onClick={handleEmailStatement}
                      disabled={sendingStatement || !selectedCustomer?.email}
                      style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)' }}
                    >
                      {sendingStatement ? '⏳ Sending...' : `📧 Email to ${selectedCustomer?.email || 'No email'}`}
                    </Button>
                  </div>
                </>
              ) : (
                <div style={{ padding: '3rem', textAlign: 'center', color: '#6b7280', background: '#f8fafc', borderRadius: '8px' }}>
                  <p style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>🎉 No outstanding invoices!</p>
                  <p>This customer has no unpaid balances for the selected date range.</p>
                </div>
              )}
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomersPage;
